import { useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import PhoneMockup from './PhoneMockup';

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}
function map(value, inMin, inMax, outMin, outMax) {
  return clamp(
    outMin + ((value - inMin) / (inMax - inMin)) * (outMax - outMin),
    Math.min(outMin, outMax),
    Math.max(outMin, outMax)
  );
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}

const LERP_SPEED = 0.06;
const PARTICLE_COUNT = 30;

export default function HeroScroll() {
  const containerRef = useRef(null);
  const progressRef = useRef({ current: 0, target: 0 });
  const particlesRef = useRef([]);
  const particleContRef = useRef(null);
  const refsMap = useRef({});

  const setRef = useCallback(
    (key) => (el) => {
      refsMap.current[key] = el;
    },
    []
  );
  const el = (key) => refsMap.current[key];

  useEffect(() => {
    const cont = particleContRef.current;
    if (!cont) return;
    cont.innerHTML = '';
    const arr = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const div = document.createElement('div');
      div.className = 'hero-particle';
      const size = 2 + Math.random() * 4;
      div.style.cssText = `left:${Math.random() * 100}%;top:${Math.random() * 100}%;width:${size}px;height:${size}px;opacity:0;`;
      cont.appendChild(div);
      arr.push({
        el: div,
        baseX: Math.random() * 100,
        baseY: Math.random() * 100,
        speed: 0.3 + Math.random() * 0.7,
        phase: Math.random() * Math.PI * 2,
      });
    }
    particlesRef.current = arr;
  }, []);

  useEffect(() => {
    let raf;
    let time = performance.now();
    const prog = progressRef.current;

    function onScroll() {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scrollableHeight = container.offsetHeight - window.innerHeight;
      prog.target = clamp(-rect.top / scrollableHeight, 0, 1);
    }

    function animate() {
      raf = requestAnimationFrame(animate);
      const t = performance.now();
      const dt = Math.min((t - time) / 16.667, 2);
      time = t;

      prog.current = lerp(prog.current, prog.target, 1 - Math.pow(1 - LERP_SPEED, dt));
      const p = prog.current;

      const s1 = map(p, 0, 0.3, 0, 1);
      const qrScale = s1 < 0.7 ? map(s1, 0, 0.15, 0.5, 1) : map(s1, 0.7, 1, 1, 0.3);
      const qrOpacity = s1 < 0.7 ? map(s1, 0, 0.1, 0, 1) : map(s1, 0.7, 1, 1, 0);
      const sceneQR = el('sceneQR');
      if (sceneQR) {
        sceneQR.style.transform = `translate(-50%,-50%) scale(${qrScale})`;
        sceneQR.style.opacity = String(qrOpacity);
      }

      const scanProg = map(s1, 0.15, 0.6, 0, 100);
      const scanLineEl = el('scanLine');
      if (scanLineEl) {
        scanLineEl.style.top = scanProg + '%';
        scanLineEl.style.opacity = s1 > 0.1 && s1 < 0.65 ? '1' : '0';
      }

      [
        { id: 'ring1', offset: 0, maxO: 0.3 },
        { id: 'ring2', offset: 0.33, maxO: 0.25 },
        { id: 'ring3', offset: 0.66, maxO: 0.2 },
      ].forEach(({ id, offset, maxO }) => {
        const ring = el(id);
        if (!ring) return;
        const pulse = ((s1 * 3 + offset) % 1);
        ring.style.transform = `translate(-50%,-50%) scale(${1 + pulse * 0.8})`;
        ring.style.opacity = String(Math.max(0, maxO - pulse * maxO) * qrOpacity);
      });

      const t1Opacity = s1 < 0.7 ? map(s1, 0.2, 0.4, 0, 1) : map(s1, 0.7, 1, 1, 0);
      const t1Y = s1 < 0.7 ? map(s1, 0.2, 0.4, 40, 0) : map(s1, 0.7, 1, 0, -30);
      const txt1 = el('text1');
      if (txt1) {
        txt1.style.opacity = String(t1Opacity);
        txt1.style.transform = `translate(-50%, ${t1Y}px)`;
      }

      const s2 = map(p, 0.3, 0.6, 0, 1);
      const t2Opacity = s2 < 0.6 ? map(s2, 0, 0.25, 0, 1) : map(s2, 0.6, 1, 1, 0);
      const t2Y = s2 < 0.6 ? map(s2, 0, 0.25, 50, 0) : map(s2, 0.6, 1, 0, -40);
      const t2Scale = s2 < 0.6 ? map(s2, 0, 0.25, 0.9, 1) : map(s2, 0.6, 1, 1, 0.95);
      const txt2 = el('text2');
      if (txt2) {
        txt2.style.opacity = String(t2Opacity);
        txt2.style.transform = `translate(-50%,-50%) translateY(${t2Y}px) scale(${t2Scale})`;
      }

      const phoneAppear = map(s2, 0.3, 0.8, 0, 1);
      const phoneScale = map(phoneAppear, 0, 1, 0.5, 0.85);
      const phoneY = map(phoneAppear, 0, 1, 100, 0);

      const s3 = map(p, 0.6, 1, 0, 1);
      const isWide = window.innerWidth > 1024;
      const finalPX = isWide ? 62 : 50;
      const phonePX = map(s3, 0, 0.5, 50, finalPX);
      const phoneFinalS = map(s3, 0, 0.5, phoneScale, isWide ? 1 : 0.85);
      const phoneOpacity = map(p, 0.4, 0.55, 0, 1);

      const phone = el('phone');
      if (phone) {
        phone.style.left = phonePX + '%';
        phone.style.transform = `translate(-50%, calc(-50% + ${phoneY * (1 - s3)}px)) scale(${phoneFinalS})`;
        phone.style.opacity = String(phoneOpacity);
      }

      const t3Opacity = map(s3, 0.15, 0.5, 0, 1);
      const t3X = map(s3, 0.15, 0.5, -60, 0);
      const txt3 = el('text3');
      if (txt3) {
        txt3.style.opacity = String(t3Opacity);
        txt3.style.transform = `translateY(-50%) translateX(${t3X}px)`;
      }

      const stats = el('stats');
      if (stats) stats.style.opacity = String(map(s3, 0.5, 0.7, 0, 1));

      const nO = map(s3, 0.55, 0.75, 0, 1);
      const notif1 = el('notif1');
      if (notif1) {
        notif1.style.opacity = String(nO);
        notif1.style.transform = `translateX(${map(nO, 0, 1, -20, 0)}px)`;
      }
      const nP = map(s3, 0.65, 0.85, 0, 1);
      const notif2 = el('notif2');
      if (notif2) {
        notif2.style.opacity = String(nP);
        notif2.style.transform = `translateX(${map(nP, 0, 1, 20, 0)}px)`;
      }

      const scrollInd = el('scrollInd');
      if (scrollInd) scrollInd.style.opacity = String(map(p, 0, 0.08, 1, 0));

      const g1 = el('glow1');
      if (g1) g1.style.transform = `translate(${-p * 80}px, ${p * 60}px)`;
      const g2 = el('glow2');
      if (g2) g2.style.transform = `translate(${p * 60}px, ${-p * 40}px)`;

      const timeS = t * 0.001;
      particlesRef.current.forEach((pt, i) => {
        const px = pt.baseX + Math.sin(timeS * pt.speed + pt.phase) * 3;
        const py = pt.baseY + Math.cos(timeS * pt.speed * 0.7 + pt.phase) * 4 - p * 15;
        const pOp = p > 0.05 ? map(p, 0.05, 0.15, 0, 0.3 + Math.sin(timeS + i) * 0.15) : 0;
        pt.el.style.transform = `translate(${px - pt.baseX}vw, ${py - pt.baseY}vh)`;
        pt.el.style.opacity = String(Math.max(0, pOp * (1 - map(p, 0.85, 1, 0, 0.5))));
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    animate();

    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div id="hero" ref={containerRef} className="hero-scroll-container">
      <div className="hero-sticky bg-neutral-950">
        <div className="hero-grid-bg" />

        <div
          ref={setRef('glow1')}
          className="absolute pointer-events-none"
          style={{
            width: 600, height: 600, borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(253,205,39,0.15),transparent 70%)',
            left: '20%', top: '10%', filter: 'blur(40px)',
          }}
        />
        <div
          ref={setRef('glow2')}
          className="absolute pointer-events-none"
          style={{
            width: 500, height: 500, borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(253,205,39,0.08),transparent 70%)',
            right: '10%', bottom: '10%', filter: 'blur(40px)',
          }}
        />

        {[
          { id: 'ring1', size: 300 },
          { id: 'ring2', size: 450 },
          { id: 'ring3', size: 600 },
        ].map(({ id, size }) => (
          <div
            key={id}
            ref={setRef(id)}
            className="scan-ring scene-el"
            style={{
              width: size, height: size,
              left: '50%', top: '50%',
              transform: 'translate(-50%,-50%)',
              opacity: 0,
            }}
          />
        ))}

        <div
          ref={particleContRef}
          className="absolute inset-0 pointer-events-none overflow-hidden"
        />

        <div
          ref={setRef('sceneQR')}
          className="scene-el"
          style={{ left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}
        >
          <div className="hero-qr-center">
            <Icon icon="solar:qr-code-bold" width={120} className="text-brand-400/70" />
            <div
              ref={setRef('scanLine')}
              className="scan-line"
              style={{ top: 0, opacity: 0 }}
            />
          </div>
        </div>

        <div
          ref={setRef('text1')}
          className="scene-el text-center"
          style={{ left: '50%', top: '72%', transform: 'translate(-50%,0)', opacity: 0 }}
        >
          <p className="text-neutral-500 text-sm font-medium tracking-widest uppercase mb-3">
            SMART QR ORDER
          </p>
          <h2 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            QR 스캔 한 번이면
          </h2>
        </div>

        <div
          ref={setRef('text2')}
          className="scene-el text-center"
          style={{ left: '50%', top: '50%', transform: 'translate(-50%,-50%)', opacity: 0 }}
        >
          <p className="text-brand-400 text-sm font-medium tracking-widest uppercase mb-3">
            SMART QR ORDER
          </p>
          <h2 className="text-white text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight leading-tight">
            스캔하면 끝,
            <br />
            <span className="gradient-text">주문이 완성됩니다</span>
          </h2>
        </div>

        <div
          ref={setRef('text3')}
          className="scene-el"
          style={{
            left: '8%', top: '50%', transform: 'translateY(-50%)',
            maxWidth: 540, opacity: 0,
          }}
        >
          <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight tracking-tight mb-5">
            직원은 줄이고
            <br />
            <span className="gradient-text">매출은 올리는</span>
            <br />
            방법
          </h1>
          <p className="text-neutral-400 text-base lg:text-lg leading-relaxed mb-8 max-w-md">
            앱 설치 없이 QR 스캔 한 번이면 주문 끝. 인건비는 82% 줄이고,
            매출은 최대 300%까지 올리세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <a
              href="tel:1644-6827"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand-400 text-neutral-900 font-semibold rounded-full hover:bg-brand-500 transition-colors duration-300 text-sm"
            >
              <Icon icon="solar:phone-bold" className="text-lg" />
              전화 상담 (1644-6827)
            </a>
            <a
              href="http://pf.kakao.com/_fgiMX/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 glass-card text-white font-medium rounded-full hover:bg-white/10 transition-colors duration-300 text-sm"
            >
              <Icon icon="solar:chat-round-dots-bold" className="text-lg" />
              카카오톡 상담
            </a>
          </div>
          <div
            ref={setRef('stats')}
            className="grid grid-cols-3 gap-5 pt-7 border-t border-white/10"
            style={{ opacity: 0 }}
          >
            {[
              { val: '82%', label: '인건비 절감' },
              { val: '100%', label: '시스템 가동률' },
              { val: '3초', label: '평균 주문 시작' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-white text-xl lg:text-2xl font-bold stat-number">
                  {s.val}
                </p>
                <p className="text-neutral-500 text-xs mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div
          ref={setRef('phone')}
          className="scene-el"
          style={{
            left: '50%', top: '50%',
            transform: 'translate(-50%,-50%) scale(0.6)',
            opacity: 0,
          }}
        >
          <div className="relative">
            <div className="absolute -inset-24 bg-gradient-to-br from-brand-400/20 via-brand-400/5 to-transparent rounded-full blur-3xl" />
            <div className="absolute -inset-16 border border-brand-400/10 rounded-full animate-spin" style={{ animationDuration: '25s' }} />
            <div className="absolute -inset-28 border border-white/5 rounded-full animate-spin" style={{ animationDuration: '40s', animationDirection: 'reverse' }} />

            <div className="absolute -left-24 top-1/4 hidden lg:flex items-center gap-2.5 px-4 py-3 rounded-xl glass-card animate-float z-20">
              <div className="w-8 h-8 rounded-lg bg-brand-400/20 flex items-center justify-center">
                <Icon icon="solar:star-bold" className="text-brand-400 text-sm" />
              </div>
              <div>
                <p className="text-white text-xs font-semibold">리뷰 +127건</p>
                <p className="text-neutral-500 text-[10px]">이번 달</p>
              </div>
            </div>

            <div className="absolute -right-20 top-2/3 hidden lg:flex items-center gap-2.5 px-4 py-3 rounded-xl glass-card z-20" style={{ animation: 'float 6s ease-in-out 2s infinite' }}>
              <div className="w-8 h-8 rounded-lg bg-emerald-400/20 flex items-center justify-center">
                <Icon icon="solar:graph-up-bold" className="text-emerald-400 text-sm" />
              </div>
              <div>
                <p className="text-white text-xs font-semibold">매출 +32%</p>
                <p className="text-neutral-500 text-[10px]">전월 대비</p>
              </div>
            </div>

            <div className="absolute -top-8 -right-8 w-3 h-3 rounded-full bg-brand-400/40" />
            <div className="absolute -bottom-6 -left-10 w-2 h-2 rounded-full bg-brand-400/30" />
            <div className="absolute top-1/2 -right-14 w-1.5 h-1.5 rounded-full bg-white/20" />

            <PhoneMockup />
          </div>
        </div>

        <div
          ref={setRef('scrollInd')}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
        >
          <span className="text-neutral-500 text-xs font-medium tracking-widest uppercase">
            Scroll
          </span>
          <div className="w-5 h-8 rounded-full border border-neutral-600 flex items-start justify-center p-1">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-bounce" />
          </div>
        </div>
      </div>
    </div>
  );
}
